import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { TOAST_DURATION } from 'src/app/config/global.config';
import { CommonService } from 'src/app/shared/common.service';
import {
  ISegregationPreviewModalData,
  SegregationPreviewModalComponent,
  SegregationPreviewResult,
} from '../components/segregation-preview-modal/segregation-preview-modal.component';
import { QuotesService } from './quotations.services';

/** Shape of the `segregation_check` the details PATCH embeds in its response. */
export interface ISegregationCheck {
  applicable: boolean;
  mechanism: ISegregationPreviewModalData['preview']['mechanism'];
  groups: ISegregationPreviewModalData['preview']['groups'];
}

/** What the caller needs to know once the preview modal is done with. */
export type SegregationPreviewOutcome = 'split' | 'dismissed';

/**
 * Owns the "a splitting rule applies to this order — split it into N?" flow:
 * the preview modal's dialog config, the split-in-place call and its error toast,
 * plus the on-demand applicability check that lets a surface re-offer the split
 * after the modal was dismissed.
 *
 * Extracted from `DetailsQuotesComponent` (WB-7569) because the side-by-side
 * correction panel now needs the same flow. Before the extraction only the
 * order-details page consumed `segregation_check`, so a company matched manually
 * from Smart Inbox silently skipped the split and approved one undivided order.
 *
 * Callers own what happens *after* the outcome (refetch, panel state, dialog
 * sequencing) — those differ per surface and are deliberately not encoded here.
 */
@Injectable({ providedIn: 'root' })
export class SegregationPreviewService {
  constructor(
    private dialog: MatDialog,
    private quotationsServices: QuotesService,
    private commonService: CommonService,
    private translateService: TranslateService,
  ) {}

  /** True when a details-PATCH response carries an actionable split proposal. */
  isApplicable(check: any): check is ISegregationCheck {
    return !!check?.applicable && !!check?.mechanism && !!check?.groups?.length;
  }

  /**
   * Opens the preview modal and, on confirm, performs the split in place.
   *
   * Emits exactly once: `'split'` when the order was actually split server-side,
   * `'dismissed'` for skip / cancel / a failed split. A failed split emits
   * `'dismissed'` on purpose — the order is unchanged, so the caller should keep
   * treating the split as still pending.
   */
  openPreview(
    orderId: number,
    orderName: string,
    preview: ISegregationPreviewModalData['preview'],
  ): Observable<SegregationPreviewOutcome> {
    const data: ISegregationPreviewModalData = { orderId, orderName, preview };

    return this.dialog.open(SegregationPreviewModalComponent, {
      data,
      width: '720px',
      maxWidth: '95vw',
      panelClass: 'spm-dialog-container',
      disableClose: true,
    }).afterClosed().pipe(
      switchMap((result: SegregationPreviewResult): Observable<SegregationPreviewOutcome> => {
        if (result?.action !== 'confirm') return of<SegregationPreviewOutcome>('dismissed');
        return this.quotationsServices.segregationSplitInPlace(orderId, result.mechanism_id).pipe(
          map(() => 'split' as SegregationPreviewOutcome),
          catchError(() => {
            this.commonService.showToast(
              this.translateService.instant('alerts.quotation.edit.error'),
              'ERROR',
              { duration: TOAST_DURATION },
            );
            return of<SegregationPreviewOutcome>('dismissed');
          }),
        );
      }),
    );
  }

  /**
   * Asks the backend whether a splitting rule currently applies to this order.
   *
   * Used to (re)build the "split applies" tag on load, so dismissing the modal is
   * never terminal and an order whose company was matched in an earlier session
   * still surfaces the proposal. Errors resolve to `null` — the tag is an
   * affordance, never a blocker.
   */
  checkApplicable(
    orderId: number,
    partnerId: number,
    companyId?: number,
  ): Observable<ISegregationCheck | null> {
    return this.quotationsServices.previewSegregationForPartner(orderId, partnerId, companyId).pipe(
      map((res: any) => {
        const check = res?.object ?? res;
        return this.isApplicable(check) ? check : null;
      }),
      catchError(() => of(null)),
    );
  }
}
